import { useTranslation } from 'react-i18next';

import { useConnection } from '@/lib/connection';
import { useOutbox } from '@/lib/useOutbox';

/**
 * Amber bar fixed to the top of the viewport whenever the
 * effective connectivity state is "should not send" — meaning the
 * navigator is offline OR the API health-check has failed
 * recently (see connection.ts). Renders nothing when shouldSend
 * is true.
 *
 * Two-line layout: primary message ("You're offline...") plus a
 * smaller subtitle. The subtitle is conditional on outbox state
 * (sub-commit 4b) — when items are pending, surface the actual
 * count via the i18next plural-suffix interpolation; when empty,
 * fall back to the generic "showing saved data" note. This
 * replaces a less informative always-on subtitle with concrete
 * affordance when it matters.
 *
 * Sits above the route content (see main.tsx mount order); the
 * fixed positioning means the page itself doesn't need to leave
 * room.
 */
export function OfflineBanner(): JSX.Element | null {
  const { t } = useTranslation('common');
  const { shouldSend } = useConnection();
  const { items } = useOutbox();

  if (shouldSend) return null;

  // Failed-permanent items are NOT pending — they require user
  // intervention (manual retry or dismiss). Only count what's
  // actually waiting to send.
  const pendingCount = items.filter(
    (i) => i.status === 'queued' || i.status === 'failed-retryable',
  ).length;

  const subtitle =
    pendingCount > 0
      ? t('offline.queuedCount', { count: pendingCount })
      : t('offline.cachedDataNote');

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-0 top-0 z-40 flex items-center gap-2 bg-amber-500 px-4 py-2 text-sm font-medium text-amber-950 shadow-md"
    >
      <svg
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-4 w-4 shrink-0"
      >
        <line x1="1" y1="1" x2="23" y2="23" />
        <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
        <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
        <path d="M10.71 5.05A16 16 0 0 1 22.58 9" />
        <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
        <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
        <line x1="12" y1="20" x2="12.01" y2="20" />
      </svg>
      <div className="flex min-w-0 flex-col leading-snug">
        <span>{t('offline.message')}</span>
        <span className="text-xs font-normal opacity-90">{subtitle}</span>
      </div>
    </div>
  );
}
